import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import ChartGroupProvider from "@galaxy-io/dls/charts/ChartGroupProvider";
import StatChart, { StatChartVariant } from "@galaxy-io/dls/charts/StatChart";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import InfiniteTable, { InfiniteTableSize } from "@galaxy-io/dls/table/InfiniteTable";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import Widget from "@galaxy-io/dls/widget/Widget";
import type { AudienceOverview } from "@gtm/contracts/core/people";
import { workflowProvider } from "@gtm/contracts/orchestration";
import { workflows as catalog } from "@gtm/contracts/workflows/catalog";
import { isCollectionProvider } from "@gtm/contracts/workflows/providers";
import { formatInTimeZone } from "date-fns-tz";
import type { ReactNode } from "react";
import { Link, useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { SourceLabel } from "@/components/workspace/source-icon";
import { Status } from "@/components/workspace/status";
import { TableFooter } from "@/components/workspace/table-footer";
import { TableLayout } from "@/components/workspace/table-layout";
import { ActivityChart, IngestionChart } from "@/features/observability/activity-chart";
import {
  Cards,
  CardTitle,
  Dashboard,
  Grid,
  Plot,
  TableBody,
} from "@/features/observability/dashboard-styles";
import { WorkflowLabel } from "@/features/workflows/workflow-label";

const NAMES = new Map<string, string>(catalog.map((entry) => [entry.key, entry.name]));
const KIND_LABELS: Record<string, string> = {
  issue: "Issues & PRs",
  comment: "Comments",
  star: "Stars",
  fork: "Forks",
  watcher: "Watchers",
  post: "Posts",
};
const titled = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const kindLabel = (kind: string) => KIND_LABELS[kind] ?? titled(kind);

const stamp = (value: string | null | undefined) =>
  value ? formatInTimeZone(value, "UTC", "MMM d, HH:mm") : "—";
const count = (value: number) => value.toLocaleString();
function elapsed(from: string, to: string | null) {
  const end = to ? Date.parse(to) : Date.now();
  const seconds = Math.max(0, Math.round((end - Date.parse(from)) / 1000));
  return seconds >= 3600
    ? `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
    : seconds >= 60
      ? `${Math.floor(seconds / 60)}m ${seconds % 60}s`
      : `${seconds}s`;
}
function workflowName(key: string) {
  return NAMES.get(key) ?? key;
}
/** The history tab can open a collection run by workflow id; deliveries are keyed differently. */
function runHref(run: AudienceOverview["runs"][number]) {
  const provider = workflowProvider(run.workflow_key);
  return provider && isCollectionProvider(provider)
    ? `/workflows?view=history&kind=collection&runId=${encodeURIComponent(run.workflow_id)}`
    : null;
}

const table = { canResizeColumns: true, size: InfiniteTableSize.MEDIUM } as const;

/** A bordered card holding one fixed-height table, the way the website view lays out its breakdowns. */
function TableCard({
  title,
  total,
  children,
}: {
  title: string;
  total: number;
  children: ReactNode;
}) {
  return (
    <Widget
      isFlush
      header={
        <CardTitle>
          <Text weight={TextWeight.MEDIUM}>{title}</Text>
        </CardTitle>
      }
    >
      <TableBody>
        <TableLayout footer={<TableFooter label={title} total={total} />}>{children}</TableLayout>
      </TableBody>
    </Widget>
  );
}

export function ObservabilityMetrics({ overview }: { overview: AudienceOverview }) {
  const { update } = useUrlState();
  const { metrics } = overview;
  const failed = overview.workflows.reduce((sum, entry) => sum + entry.failed, 0);
  const running = overview.workflows.reduce((sum, entry) => sum + entry.running, 0);
  const stats: Array<[string, string]> = [
    ["Active people", count(metrics.active_people)],
    ["New people", count(metrics.new_people)],
    ["Companies engaged", count(metrics.companies)],
    ["Signals observed", count(metrics.actions)],
    ["Workflow runs", count(metrics.runs)],
    ["Records fetched", count(metrics.fetched)],
    ["New records", count(metrics.created)],
    [running ? "Running now" : "Failed runs", count(running || failed)],
  ];

  return (
    <Dashboard>
      <Cards>
        {stats.map(([label, value]) => (
          <StatChart
            hasBorder
            key={label}
            label={label}
            variant={StatChartVariant.BASE}
            value={
              <Text size={TextSize.HEADING_MD} weight={TextWeight.MEDIUM}>
                {value}
              </Text>
            }
          />
        ))}
      </Cards>

      <ChartGroupProvider>
        <Grid>
          <Widget
            isFlush
            header={
              <CardTitle>
                <Text weight={TextWeight.MEDIUM}>Daily activity by kind</Text>
              </CardTitle>
            }
          >
            <Plot>
              <ActivityChart days={overview.trend} height={200} />
            </Plot>
          </Widget>
          <Widget
            isFlush
            header={
              <CardTitle>
                <Text weight={TextWeight.MEDIUM}>Records ingested per day</Text>
              </CardTitle>
            }
          >
            <Plot>
              <IngestionChart days={overview.ingestion} height={200} />
            </Plot>
          </Widget>
        </Grid>
      </ChartGroupProvider>

      <Grid>
        <TableCard title="Signals by kind" total={overview.kinds.length}>
          <InfiniteTable
            {...table}
            ariaLabel="Signals by kind"
            data={overview.kinds}
            getRowId={(row) => row.kind}
            columns={[
              {
                id: "kind",
                header: "Kind",
                accessor: (row) => kindLabel(row.kind),

                cell: ({ row }) => (
                  <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
                    {kindLabel(row.kind)}
                  </Text>
                ),
                isRowHeader: true,
              },
              {
                id: "count",
                header: "Signals",
                accessor: (row) => row.count,
                align: "right" as const,
              },
              {
                id: "people",
                header: "People",
                accessor: (row) => row.people,
                align: "right" as const,
              },
            ]}
            emptyState={<EmptyState header="No activity in this period" />}
          />
        </TableCard>
        <TableCard title="Most active companies" total={overview.companies.length}>
          <InfiniteTable
            {...table}
            ariaLabel="Most active companies"
            data={overview.companies}
            getRowId={(row) => row.id}
            columns={[
              {
                id: "name",
                header: "Company",
                accessor: (row) => row.name,

                cell: ({ row }) => (
                  <Button
                    label={row.name}
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.SMALL}
                    onClick={() => update({ companyId: row.id })}
                  />
                ),
                isRowHeader: true,
              },
              {
                id: "people",
                header: "Active people",
                accessor: (row) => row.people,
                align: "right" as const,
              },
              {
                id: "signals",
                header: "Signals",
                accessor: (row) => row.signals,
                align: "right" as const,
              },
            ]}
            emptyState={<EmptyState header="No companies were active in this period" />}
          />
        </TableCard>
      </Grid>

      <TableCard title="Sources" total={overview.sources.length}>
        <InfiniteTable
          {...table}
          ariaLabel="Sources"
          data={overview.sources}
          getRowId={(row) => row.id}
          columns={[
            {
              id: "source",
              header: "Source",
              accessor: (row) => row.resource,

              cell: ({ row }) => <SourceLabel source={row.provider} label={row.resource} />,
              isRowHeader: true,
            },
            {
              id: "state",
              header: "Collection",
              accessor: (row) => row.enabled,

              cell: ({ row }) => <Status value={row.enabled ? "active" : "paused"} />,
            },
            {
              id: "signals",
              header: "Signals",
              accessor: (row) => row.signals,
              align: "right" as const,
            },
            {
              id: "people",
              header: "People",
              accessor: (row) => row.people,
              align: "right" as const,
            },
            {
              id: "total",
              header: "Fetched all time",
              accessor: (row) => row.total,
              align: "right" as const,
            },
            {
              id: "synced",
              header: "Last synced (UTC)",
              accessor: (row) => row.last_sync_at ?? "",

              cell: ({ row }) => <Text size={TextSize.BODY_SM}>{stamp(row.last_sync_at)}</Text>,
            },
          ]}
          emptyState={<EmptyState header="No sources are connected" />}
        />
      </TableCard>

      <TableCard title="Workflows" total={overview.workflows.length}>
        <InfiniteTable
          {...table}
          ariaLabel="Workflows"
          data={overview.workflows}
          getRowId={(row) => row.key}
          columns={[
            {
              id: "workflow",
              header: "Workflow",
              accessor: (row) => workflowName(row.key),

              cell: ({ row }) => (
                <WorkflowLabel
                  source={workflowProvider(row.key) ?? ""}
                  name={workflowName(row.key)}
                />
              ),
              isRowHeader: true,
            },
            {
              id: "status",
              header: "Last outcome",
              accessor: (row) => row.last_status ?? "",

              cell: ({ row }) =>
                row.last_status ? (
                  <Status value={row.last_status} />
                ) : (
                  <Text size={TextSize.BODY_SM}>—</Text>
                ),
            },
            { id: "runs", header: "Runs", accessor: (row) => row.runs, align: "right" as const },
            {
              id: "failed",
              header: "Failed",
              accessor: (row) => row.failed,
              align: "right" as const,
            },
            {
              id: "fetched",
              header: "Fetched",
              accessor: (row) => row.fetched,
              align: "right" as const,
            },
            {
              id: "created",
              header: "New records",
              accessor: (row) => row.created,
              align: "right" as const,
            },
            {
              id: "last",
              header: "Last run (UTC)",
              accessor: (row) => row.last_run_at ?? "",

              cell: ({ row }) => <Text size={TextSize.BODY_SM}>{stamp(row.last_run_at)}</Text>,
            },
          ]}
          emptyState={<EmptyState header="No workflows ran in this period" />}
        />
      </TableCard>

      <TableCard title="Recent runs" total={overview.runs.length}>
        <InfiniteTable
          {...table}
          ariaLabel="Recent runs"
          data={overview.runs}
          getRowId={(row) => row.run_id}
          columns={[
            {
              id: "workflow",
              header: "Workflow",
              accessor: (row) => workflowName(row.workflow_key),

              cell: ({ row }) => {
                const label = (
                  <WorkflowLabel
                    source={workflowProvider(row.workflow_key) ?? ""}
                    name={workflowName(row.workflow_key)}
                  />
                );
                const href = runHref(row);
                return href ? <Link href={href}>{label}</Link> : label;
              },
              isRowHeader: true,
            },
            { id: "source", header: "Source", accessor: (row) => row.source_name },
            {
              id: "status",
              header: "Status",
              accessor: (row) => row.status,

              cell: ({ row }) => <Status value={row.status} />,
            },
            { id: "trigger", header: "Trigger", accessor: (row) => row.trigger },
            {
              id: "fetched",
              header: "Fetched",
              accessor: (row) => row.fetched,
              align: "right" as const,
            },
            {
              id: "created",
              header: "New records",
              accessor: (row) => row.created,
              align: "right" as const,
            },
            {
              id: "started",
              header: "Started (UTC)",
              accessor: (row) => row.started_at,

              cell: ({ row }) => <Text size={TextSize.BODY_SM}>{stamp(row.started_at)}</Text>,
            },
            {
              id: "duration",
              header: "Duration",
              accessor: (row) => row.started_at,
              align: "right" as const,

              cell: ({ row }) => (
                <Text size={TextSize.BODY_SM}>{elapsed(row.started_at, row.closed_at)}</Text>
              ),
            },
            {
              id: "error",
              header: "Error",
              accessor: (row) => row.error ?? "",

              cell: ({ row }) => (
                <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={1}>
                  {row.error ?? ""}
                </Text>
              ),
            },
          ]}
          emptyState={<EmptyState header="No runs in this period" />}
        />
      </TableCard>

      <TableCard title="Most active people" total={overview.people.length}>
        <InfiniteTable
          {...table}
          ariaLabel="Most active people"
          data={overview.people}
          getRowId={(row) => row.id}
          columns={[
            {
              id: "name",
              header: "Person",
              accessor: (row) => row.name,

              cell: ({ row }) => (
                <Button
                  label={row.name}
                  variant={ButtonVariant.TERTIARY}
                  size={ButtonSize.SMALL}
                  onClick={() => update({ personId: row.id })}
                />
              ),
              isRowHeader: true,
            },
            {
              id: "company",
              header: "Company",
              accessor: (row) => row.company ?? "",

              cell: ({ row }) =>
                row.company_id ? (
                  <Button
                    label={row.company ?? "Company"}
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.SMALL}
                    onClick={() => update({ companyId: row.company_id })}
                  />
                ) : (
                  <Text size={TextSize.BODY_SM}>—</Text>
                ),
            },
            {
              id: "signals",
              header: "Signals",
              accessor: (row) => row.signals,
              align: "right" as const,
            },
            {
              id: "seen",
              header: "Last seen (UTC)",
              accessor: (row) => row.last_seen_at,

              cell: ({ row }) => <Text size={TextSize.BODY_SM}>{stamp(row.last_seen_at)}</Text>,
            },
          ]}
          emptyState={<EmptyState header="No people were active in this period" />}
        />
      </TableCard>

      <TableCard title="Daily activity" total={overview.trend.length}>
        <InfiniteTable
          {...table}
          ariaLabel="Daily activity"
          data={[...overview.trend].reverse()}
          getRowId={(day) => day.day}
          columns={[
            {
              id: "day",
              header: "Date",
              accessor: (day) => day.day,

              cell: ({ row }) => (
                <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
                  {formatInTimeZone(row.day, "UTC", "MMM d, yyyy")}
                </Text>
              ),
              isRowHeader: true,
            },
            {
              id: "people",
              header: "Active people",
              accessor: (d) => d.people,
              align: "right" as const,
            },
            {
              id: "actions",
              header: "Signals",
              accessor: (d) => d.actions,
              align: "right" as const,
            },
            {
              id: "issues",
              header: "Issues & PRs",
              accessor: (d) => d.issues,
              align: "right" as const,
            },
            {
              id: "comments",
              header: "Comments",
              accessor: (d) => d.comments,
              align: "right" as const,
            },
            { id: "stars", header: "Stars", accessor: (d) => d.stars, align: "right" as const },
            {
              id: "discovered",
              header: "Backfilled",
              accessor: (d) => d.discovered,
              align: "right" as const,
            },
          ]}
          emptyState={<EmptyState header="No activity in this period" />}
        />
      </TableCard>
    </Dashboard>
  );
}
